class ProductSwipeGallery {
    constructor() {
        this.galleries = new Map();
        this._observer = null;
        this.init();
    }

    init() {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.setupGalleries());
        } else {
            this.setupGalleries();
        }

        // Re-inicjalizuj po resize
        let resizeTimeout;
        window.addEventListener('resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => this.setupGalleries(), 250);
        });

        // Wishlista doładowuje karty async (WishlistView w global.js) — podpinaj po fakcie
        document.addEventListener('wishlist:items-loaded', () => this.setupGalleries());
    }

    setupGalleries() {
        // Tylko strony kolekcji i wishlista - nie homepage
        const isWishlistPage = !!document.querySelector('[data-wishlist-container]');
        if (!window.location.pathname.includes('/collections/') && !isWishlistPage) {
            return;
        }
        
        // Tylko na mobile
        if (window.innerWidth > 768) {
            // Cleanup existing galleries na desktop
            this.cleanupAllGalleries();
            return;
        }
        
        // Lazy-init: NIE budujemy kontenerów swipe dla wszystkich kart na starcie.
        // Wcześniej setupGalleries() synchronicznie wstrzykiwał <img> dla każdego
        // zdjęcia każdej karty i czytał offsetWidth — to dokładało się do render
        // delay kolekcji na mobile. Teraz każdą kartę inicjalizujemy dopiero gdy
        // zbliża się do viewportu (IntersectionObserver), poza ścieżką renderu.
        const swipeDataElements = document.querySelectorAll('.mobile-swipe-data');
        if (!swipeDataElements.length) return;

        if (this._observer) this._observer.disconnect();

        // Kontener karty (renderowany element z boxem). Uwaga: .mobile-swipe-data to
        // <script type="application/json"> — NIE MA boxa layoutu, więc obserwowanie go
        // przez IntersectionObserver nigdy by nie odpaliło. Obserwujemy kartę.
        const cardOf = (el) =>
            el.closest('.pc-card__media') ||
            el.closest('.product-item') ||
            el.closest('.card--block-media') ||
            el.closest('.card__inner');

        if (!('IntersectionObserver' in window)) {
            // Fallback dla starszych przeglądarek — zachowanie jak dawniej.
            swipeDataElements.forEach(el => this.buildGalleryFromData(el));
            return;
        }

        this._observer = new IntersectionObserver((entries, obs) => {
            entries.forEach(entry => {
                if (!entry.isIntersecting) return;
                obs.unobserve(entry.target);
                const dataEl = entry.target.querySelector('.mobile-swipe-data');
                if (dataEl) this.buildGalleryFromData(dataEl);
            });
        }, { rootMargin: '300px 0px' });

        swipeDataElements.forEach(el => {
            const card = cardOf(el);
            if (card) this._observer.observe(card);
            else this.buildGalleryFromData(el);
        });
    }

    buildGalleryFromData(dataElement) {
        const productId = dataElement.getAttribute('data-product-id');
        // Ella 6 (Naoko): .product-item  |  Ella 7: .card--block-media or .card__inner
        // Naoko redesign (listing .pc-card): kontener swipe to .pc-card__media
        const productCard =
            dataElement.closest('.pc-card__media') ||
            dataElement.closest('.product-item') ||
            dataElement.closest('.card--block-media') ||
            dataElement.closest('.card__inner');

        if (!productCard || this.galleries.has(productId)) return;

        try {
            const imagesData = JSON.parse(dataElement.textContent);
            this.setupProductSwipe(productCard, productId, imagesData.images);
        } catch (error) {
            // Błąd parsowania danych obrazów
        }
    }

    cleanupAllGalleries() {
        if (this._observer) {
            this._observer.disconnect();
            this._observer = null;
        }
        this.galleries.clear();
    }

    setupProductSwipe(productCard, productId, images) {
        if (images.length <= 1) return;

        const gallery = {
            productCard,
            images,
            currentIndex: 0,
            mainImage: productCard.querySelector('img:last-of-type'),
            // Ella 6: .card-link  |  Ella 7: .link-wrap  |  Naoko .pc-card: .pc-card__media-link
            cardLink: productCard.querySelector('.card-link, .link-wrap, .pc-card__media-link'),
            isActive: false,
            startX: 0,
            startY: 0,
            startTime: 0,
            hasMoved: false,
            isScrolling: false,
            isSwipeDetected: false,
            isDragging: false,
            currentTransform: 0,
            permanentContainer: null,
            imageTrack: null
        };

        if (!gallery.mainImage) return;

        // Nowe podejście: utwórz stały kontener zamiast modyfikować oryginalny obraz
        this.createPermanentContainer(gallery);

        // Dodaj touch events
        this.addTouchEvents(gallery);

        this.galleries.set(productId, gallery);
    }

    createPermanentContainer(gallery) {
        // Sprawdź czy kontener już istnieje
        if (gallery.productCard.querySelector('.swipe-permanent-container')) return;

        const container = document.createElement('div');
        container.className = 'swipe-permanent-container';
        container.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            z-index: 1;
            pointer-events: none;
            overflow: hidden;
        `;

        const imageTrack = document.createElement('div');
        imageTrack.className = 'image-track';
        imageTrack.style.cssText = `
            display: flex;
            width: ${gallery.images.length * 100}%;
            height: 100%;
            transition: transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
            will-change: transform;
        `;

        // Stwórz obrazy dla każdego zdjęcia w galerii
        gallery.images.forEach((imageData, index) => {
            const imageWrapper = document.createElement('div');
            imageWrapper.className = 'image-wrapper';
            imageWrapper.style.cssText = `
                width: ${100 / gallery.images.length}%;
                height: 100%;
                flex-shrink: 0;
                display: flex;
                align-items: center;
                justify-content: center;
            `;

            const img = document.createElement('img');
            const normalizedSrc = imageData.src.startsWith('//') ? 'https:' + imageData.src : imageData.src;

            // Perf hybryd: ładujemy od razu tylko slajd 0 (widoczny) i 1 (następny w
            // kolejce — żeby pierwszy swipe był natychmiastowy). Slajdy 2+ trzymamy w
            // data-src i dociągamy NA ŻĄDANIE przy nawigacji (loadSwipeImage), zawsze
            // jeden do przodu. Kolekcja ściągała komplet zdjęć każdej karty (~10 MB),
            // choć większość userów nie swipe'uje — to zdejmuje ten balast z listingu.
            if (index < 2) {
                img.src = normalizedSrc;
                img.srcset = normalizedSrc;
            } else {
                img.dataset.src = normalizedSrc;
            }
            img.alt = imageData.alt;
            img.style.cssText = `
                width: 100%;
                height: 100%;
                object-fit: cover;
                display: block;
            `;

            imageWrapper.appendChild(img);
            imageTrack.appendChild(imageWrapper);
        });

        container.appendChild(imageTrack);
        
        // Znajdź odpowiedni kontener nadrzędny i dodaj stały kontener.
        // Używamy productCard (.card--block-media / .card__inner) — pełna szerokość
        // image-wrappera, bez zmiany flex-layoutu siostrzanych elementów (badge'y).
        const parentContainer = gallery.productCard;
        parentContainer.style.position = 'relative';
        parentContainer.appendChild(container);
        
        // Ukryj oryginalny obraz
        gallery.mainImage.style.opacity = '0';
        gallery.mainImage.style.pointerEvents = 'none';
        
        // Ustaw początkową pozycję (pierwszy obraz)
        const containerWidth = container.offsetWidth;
        const targetTransform = -(gallery.currentIndex * containerWidth);
        imageTrack.style.transform = `translate3d(${targetTransform}px, 0, 0)`;
        
        // Zapisz referencje
        gallery.permanentContainer = container;
        gallery.imageTrack = imageTrack;
        gallery.currentTransform = targetTransform;
    }

    addTouchEvents(gallery) {
        const handleStart = (e) => {
            const clientX = e.touches ? e.touches[0].clientX : e.clientX;
            const clientY = e.touches ? e.touches[0].clientY : e.clientY;
            
            gallery.startX = clientX;
            gallery.startY = clientY;
            gallery.startTime = Date.now();
            gallery.hasMoved = false;
            gallery.isScrolling = false;
            gallery.isSwipeDetected = false;
            gallery.isDragging = false;
            gallery.isActive = e.type.includes('mouse') || e.type.includes('touch');

            // Dociągnij sąsiednie slajdy zanim ruszy przeciąganie, żeby w żadną stronę
            // nie pokazał się pusty kadr. Slajdy dalej niż ±1 nadal czekają na dojście.
            this.loadSwipeImage(gallery, gallery.currentIndex + 1);
            this.loadSwipeImage(gallery, gallery.currentIndex - 1);
        };

        const handleMove = (e) => {
            if (!gallery.isActive || gallery.isScrolling) return;
            
            const clientX = e.touches ? e.touches[0].clientX : e.clientX;
            const clientY = e.touches ? e.touches[0].clientY : e.clientY;
            
            const deltaX = Math.abs(clientX - gallery.startX);
            const deltaY = Math.abs(clientY - gallery.startY);
            const rawDeltaX = clientX - gallery.startX;
            
            if (!gallery.hasMoved) {
                // Pierwszy ruch - określ kierunek
                if (deltaY > deltaX && deltaY > 10) {
                    gallery.isScrolling = true;
                    return;
                }
                if (deltaX > 10 && deltaX > deltaY) {
                    gallery.hasMoved = true;
                    gallery.isSwipeDetected = true;
                    gallery.isDragging = true;
                    
                    // Wyłącz transition podczas przeciągania
                    if (gallery.imageTrack) {
                        gallery.imageTrack.style.transition = 'none';
                    }
                    
                    // Teraz blokuj pointer events - tylko gdy wykryto horizontal swipe
                    if (gallery.cardLink) {
                        gallery.cardLink.style.pointerEvents = 'none';
                    }
                }
            }
            
            // Zapobiegaj scrollowaniu dla horizontal swipe
            if (gallery.isSwipeDetected && deltaX > deltaY) {
                e.preventDefault();
            }
            
            // Animuj w czasie rzeczywistym podczas przeciągania
            if (gallery.isDragging && gallery.imageTrack) {
                const containerWidth = gallery.permanentContainer.offsetWidth;
                const currentOffset = -(gallery.currentIndex * containerWidth);
                
                // Ogranicz ruch żeby nie wychodzić za granice
                let dragOffset = rawDeltaX;
                const maxDrag = containerWidth * 0.3; // Maksymalnie 30% szerokości
                
                // Jeśli jesteśmy na pierwszym obrazie i przeciągamy w prawo
                if (gallery.currentIndex === 0 && rawDeltaX > 0) {
                    dragOffset = Math.min(rawDeltaX * 0.3, maxDrag);
                }
                // Jeśli jesteśmy na ostatnim obrazie i przeciągamy w lewo
                else if (gallery.currentIndex === gallery.images.length - 1 && rawDeltaX < 0) {
                    dragOffset = Math.max(rawDeltaX * 0.3, -maxDrag);
                }
                
                const newTransform = currentOffset + dragOffset;
                gallery.currentTransform = newTransform;
                gallery.imageTrack.style.transform = `translate3d(${newTransform}px, 0, 0)`;
            }
        };

        const handleEnd = (e) => {
            const wasSwiping = gallery.isSwipeDetected;
            const wasDragging = gallery.isDragging;
            
            if (!gallery.isActive || gallery.isScrolling || !gallery.hasMoved) {
                gallery.isActive = false;
                gallery.isDragging = false;
                return;
            }
            
            const clientX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
            const deltaX = clientX - gallery.startX;
            const deltaTime = Date.now() - gallery.startTime;
            
            // Przywróć transition po zakończeniu przeciągania
            if (gallery.imageTrack) {
                gallery.imageTrack.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
            }
            
            // Sprawdź czy to był prawidłowy swipe
            const isValidSwipe = Math.abs(deltaX) > 50 && deltaTime < 600 && wasSwiping;
            const shouldChange = isValidSwipe || (gallery.permanentContainer && Math.abs(deltaX) > gallery.permanentContainer.offsetWidth * 0.3);
            
            if (shouldChange) {
                // Zapobiegaj kliku card-link po swipe
                e.preventDefault();
                e.stopPropagation();
                
                if (deltaX > 0 && gallery.currentIndex > 0) {
                    this.previousImage(gallery);
                } else if (deltaX < 0 && gallery.currentIndex < gallery.images.length - 1) {
                    this.nextImage(gallery);
                } else {
                    // Wróć do pozycji wyjściowej jeśli próba wyjścia poza granice
                    this.snapToCurrentImage(gallery);
                }
            } else if (wasDragging) {
                // Wróć do pozycji wyjściowej jeśli swipe był za słaby
                this.snapToCurrentImage(gallery);
            }
            
            // Przywróć card-link tylko jeśli został zablokowany
            if (wasSwiping && gallery.cardLink) {
                setTimeout(() => {
                    gallery.cardLink.style.pointerEvents = 'auto';
                }, 50);
            }
            
            gallery.isActive = false;
            gallery.isSwipeDetected = false;
            gallery.isDragging = false;
        };


        
        // Touch events
        gallery.productCard.addEventListener('touchstart', handleStart, { passive: true });
        gallery.productCard.addEventListener('touchmove', handleMove, { passive: false });
        gallery.productCard.addEventListener('touchend', handleEnd, { passive: false });
        
        // Mouse events (dla testowania na desktop)
        gallery.productCard.addEventListener('mousedown', handleStart);
        gallery.productCard.addEventListener('mousemove', handleMove, { passive: false });
        gallery.productCard.addEventListener('mouseup', handleEnd);
        gallery.productCard.addEventListener('mouseleave', handleEnd);
    }

    snapToCurrentImage(gallery) {
        if (!gallery.imageTrack) return;
        
        // Włącz transition dla snap animacji
        gallery.imageTrack.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        
        const containerWidth = gallery.permanentContainer.offsetWidth;
        const targetTransform = -(gallery.currentIndex * containerWidth);
        gallery.imageTrack.style.transform = `translate3d(${targetTransform}px, 0, 0)`;
        gallery.currentTransform = targetTransform;
    }

    nextImage(gallery) {
        gallery.lastSwipeDirection = 'left';
        gallery.currentIndex = (gallery.currentIndex + 1) % gallery.images.length;
        this.updateImage(gallery);
    }

    previousImage(gallery) {
        gallery.lastSwipeDirection = 'right';
        gallery.currentIndex = gallery.currentIndex === 0 
            ? gallery.images.length - 1 
            : gallery.currentIndex - 1;
        this.updateImage(gallery);
    }

    updateImage(gallery) {
        if (!gallery.imageTrack) return;

        // Włącz transition dla animacji zmiany obrazu
        gallery.imageTrack.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';

        const containerWidth = gallery.permanentContainer.offsetWidth;
        const targetTransform = -(gallery.currentIndex * containerWidth);
        gallery.imageTrack.style.transform = `translate3d(${targetTransform}px, 0, 0)`;
        gallery.currentTransform = targetTransform;

        // Dociągnij bieżący slajd i jego sąsiadów (jeden do przodu w zapasie).
        this.loadSwipeImage(gallery, gallery.currentIndex);
        this.loadSwipeImage(gallery, gallery.currentIndex + 1);
        this.loadSwipeImage(gallery, gallery.currentIndex - 1);
    }

    // Dociąga zdjęcie slajdu na żądanie (slajdy 2+ startują z data-src bez ładowania).
    // Indeks zawijany, żeby pasował do nawigacji z zawijaniem (nextImage/previousImage).
    loadSwipeImage(gallery, index) {
        const n = gallery.images ? gallery.images.length : 0;
        if (!n || !gallery.imageTrack) return;
        const idx = ((index % n) + n) % n;
        const img = gallery.imageTrack.querySelectorAll('img')[idx];
        if (img && !img.getAttribute('src') && img.dataset.src) {
            img.src = img.dataset.src;
            img.srcset = img.dataset.src;
        }
    }
}

// Inicjalizuj gdy DOM jest gotowy
new ProductSwipeGallery();